export enum ProductChoiceType {
  DROPDOWN = 'DROPDOWN',
  RADIO = 'RADIO',
  IMAGE = 'IMAGE',
  SWATCH = 'SWATCH',
}

type Choice = {
  key: string
  disabled: boolean
  colour?: string | null
  image?: string | null
  notifyWhenInStockEnabled?: boolean | null
}

type Options = {
  key: string
  label: string
  type: ProductChoiceType
  choices: Choice[]
  placeholder?: string | null
  attribute?: boolean | null
}[]

export interface Product {
  options: {
    key: string
    choices: {
      key: string
      optionKey: string
      title: string
      colour?: string | null
      image?: string
    }[]
  }[]
  variants: {
    sku: number
    inStock: boolean
    images: {
      thumbnail?: string
    }[]
    choices: {
      key: string
      optionKey: string
      colour?: string
    }[]
    notifyWhenInStockEnabled?: boolean | null
  }[]
  linkedOn?: string | null
}

export type VariantChoices = {
  [variantSKU: number]: Options
}

export type TransformedChoice = Choice & {
  disabled: boolean
  inStock: boolean
  thumbnailImage?: string
  notifyWhenInStockEnabled?: boolean | null
}

export type ChoiceMap = {
  [key: string]: { [optionKey: string]: TransformedChoice[] }
}

const generateChoicesKey = (choices: { key: string }[]) => {
  return choices
    .map((choice) => choice.key)
    .sort()
    .join(',')
}

export const productOptions = (
  product: Product,
  productOptionTypeMap: {
    [key: string]: {
      type: ProductChoiceType
      propertyKey: string
      placeholderPropertyKey: string
    }
  },
  siteProperties: { [key: string]: { value: string } },
  showOutOfStockAsDisabled?: boolean,
  optionKeysToFilter?: string[],
  optionKeysToApplyAttribute?: string[],
  enablePreselectedProductVariant?: boolean,
): {
  options: VariantChoices
  choiceMap: ChoiceMap
} => {
  if (!product?.options) {
    return { options: {}, choiceMap: {} }
  }

  type VariantMetaMap = {
    [choiceKey: string]: {
      inStock: boolean
      disabled: boolean
      supportedChoices: {
        [optionKey: string]: TransformedChoice[]
      } | null
      notifyWhenInStockEnabled?: boolean
      thumbnailImage?: string
    }
  }

  const updateChoiceMapKeyValue = ({
    variant,
    storedKeys,
    choice,
    otherChoice,
    accumulator,
  }) => {
    if (!storedKeys.includes(otherChoice.key)) {
      accumulator[choice.key][otherChoice.optionKey].push({
        ...otherChoice,
        disabled: showOutOfStockAsDisabled ? !variant.inStock : false,
        inStock: variant.inStock,
        thumbnailImage: variant.images[0]?.thumbnail,
        notifyWhenInStockEnabled: variant.notifyWhenInStockEnabled ?? false,
      })
      const optionKeys = product.options.reduce<string[]>(
        (accumulator, option) => {
          if (option.key !== otherChoice.optionKey) {
            return accumulator
          }
          option.choices.forEach((choice) => accumulator.push(choice.key))
          return accumulator
        },
        [],
      )
      accumulator[choice.key][otherChoice.optionKey].sort((choiceA, choiceB) =>
        optionKeys.indexOf(choiceA.key) > optionKeys.indexOf(choiceB.key)
          ? 1
          : -1,
      )
    }
  }

  const createChoiceMapKey = ({ accumulator, choice, otherChoice }) => {
    if (!accumulator[choice.key]) {
      accumulator[choice.key] = {}
    }

    if (!accumulator[choice.key][otherChoice.optionKey]) {
      accumulator[choice.key][otherChoice.optionKey] = []
    }
  }

  const choiceMap = product.variants.reduce<ChoiceMap>(
    (accumulator, variant) => {
      variant.choices.forEach((choice) => {
        if (product.linkedOn) {
          variant.choices.forEach((otherChoice) => {
            if (choice.optionKey === otherChoice.optionKey) {
              return
            }

            if (choice.optionKey === product.linkedOn) {
              createChoiceMapKey({ accumulator, choice, otherChoice })
              createChoiceMapKey({
                accumulator,
                otherChoice: choice,
                choice: otherChoice,
              })

              const storedKeys = accumulator[choice.key][
                otherChoice.optionKey
              ].map((choice) => choice.key)

              updateChoiceMapKeyValue({
                variant,
                storedKeys,
                choice,
                otherChoice,
                accumulator,
              })
              updateChoiceMapKeyValue({
                variant,
                storedKeys,
                accumulator,
                otherChoice: choice,
                choice: otherChoice,
              })
            }

            return accumulator
          })
        } else {
          if (!accumulator[choice.optionKey]) {
            accumulator[choice.optionKey] = {}
          }

          if (!accumulator[choice.optionKey][choice.optionKey]) {
            accumulator[choice.optionKey][choice.optionKey] = []
          }

          const storedKeys = accumulator[choice.optionKey][
            choice.optionKey
          ].map((choice) => choice.key)

          if (!storedKeys.includes(choice.key)) {
            accumulator[choice.optionKey][choice.optionKey].push({
              ...choice,
              disabled: showOutOfStockAsDisabled ? !variant.inStock : false,
              inStock: variant.inStock,
              thumbnailImage: variant.images[0]?.thumbnail,
              notifyWhenInStockEnabled:
                variant.notifyWhenInStockEnabled ?? false,
            })
            const optionKeys = product.options.reduce<string[]>(
              (accumulator, option) => {
                if (option.key !== choice.optionKey) {
                  return accumulator
                }
                option.choices.forEach((choice) => accumulator.push(choice.key))
                return accumulator
              },
              [],
            )
            if (
              accumulator[choice.optionKey][choice.optionKey].length ===
              optionKeys?.length
            ) {
              accumulator[choice.optionKey][
                choice.optionKey
              ].sort((choiceA, choiceB) =>
                optionKeys.indexOf(choiceA.key) >
                optionKeys.indexOf(choiceB.key)
                  ? 1
                  : -1,
              )
            }
          }
        }
      })

      return accumulator
    },
    {},
  )

  const variantMetaMap = product.variants.reduce<VariantMetaMap>(
    (accumulator, variant) => {
      const linkedVariantChoice = product.linkedOn
        ? variant.choices.find(
            (choice) => choice.optionKey === product.linkedOn,
          )
        : null

      if (
        linkedVariantChoice &&
        !accumulator[generateChoicesKey([linkedVariantChoice])]
      ) {
        accumulator[generateChoicesKey([linkedVariantChoice])] = {
          supportedChoices: {},
          inStock: false,
          thumbnailImage: variant.images[0]?.thumbnail,
          disabled: true,
        }
      }

      let supportedChoices: {
        [optionKey: string]: TransformedChoice[]
      } | null = null

      if (Object.keys(choiceMap).length) {
        if (linkedVariantChoice && choiceMap[linkedVariantChoice.key]) {
          supportedChoices = choiceMap[linkedVariantChoice.key]
        } else if (
          variant.choices.length &&
          choiceMap[variant.choices[0].optionKey]
        ) {
          supportedChoices = choiceMap[variant.choices[0].optionKey]
        }
      }

      accumulator[generateChoicesKey(variant.choices)] = {
        supportedChoices,
        inStock: variant.inStock,
        thumbnailImage: variant.images[0]?.thumbnail,
        disabled: showOutOfStockAsDisabled ? !variant.inStock : false,
        notifyWhenInStockEnabled: variant.notifyWhenInStockEnabled ?? false,
      }
      return accumulator
    },
    {},
  )

  let accumulatorInitialValue = {}

  if (!enablePreselectedProductVariant) {
    const linkedOnChoiceKeys = Object.keys(choiceMap).reduce<VariantChoices>(
      (accumulator, key) => {
        if (!accumulator[key]) {
          accumulator[key] = []

          const optionKeys = Object.keys(choiceMap[key])

          product.options.forEach((option) => {
            if (!optionKeys.includes(option.key)) {
              accumulator[key].push({
                key: option.key,
                label:
                  siteProperties[productOptionTypeMap[option.key].propertyKey]
                    ?.value || option.key,
                placeholder:
                  siteProperties[
                    productOptionTypeMap[option.key].placeholderPropertyKey
                  ]?.value || null,
                type:
                  productOptionTypeMap[option.key]?.type ||
                  ProductChoiceType.DROPDOWN,
                attribute: optionKeysToApplyAttribute?.includes(option.key)
                  ? true
                  : null,
                choices: option.choices.map((choice) => {
                  const key = generateChoicesKey([choice])

                  const image = variantMetaMap[key]?.thumbnailImage

                  return {
                    ...choice,
                    image: (choice.colour && image) || null,
                    inStock: true,
                    disabled: false,
                  }
                }),
              })
            } else {
              const choicesFromHorizon =
                product.options.find(
                  (otherOption) => otherOption.key === option.key,
                )?.choices || []
              const choicesFromChoiceMap = choiceMap[key][option.key]

              const choices =
                option.key === product.linkedOn &&
                choicesFromHorizon &&
                choicesFromChoiceMap.length !== choicesFromHorizon.length
                  ? choicesFromHorizon
                  : choicesFromChoiceMap

              const transformedChoices = choices.map((choice) => {
                // @TODO: REBUILD-5103
                // Horrible workaround for short hex codes.
                // Validates they are 1-2 char hexes and pads them out
                if (
                  choice.colour &&
                  choice.colour.startsWith('#') &&
                  choice.colour.length < 7
                ) {
                  const hexStart = choice.colour.substring(1, 3)
                  const validateHex = '#'.padEnd(choice.colour.length, hexStart)
                  if (validateHex === choice.colour) {
                    choice.colour = '#'.padEnd(7, hexStart)
                  }
                }

                const choiceKey = generateChoicesKey(
                  product.options.length === 1 ? [choice] : [{ key }, choice],
                )

                let image = variantMetaMap[choiceKey]?.thumbnailImage

                if (!image) {
                  const fallbackKey = generateChoicesKey(
                    choices.filter(
                      (choice) => choice.optionKey === product.linkedOn,
                    ),
                  )
                  image = variantMetaMap[fallbackKey]?.thumbnailImage
                }

                return {
                  ...choice,
                  image: (choice.colour && image) || null,
                  inStock: variantMetaMap[choiceKey]?.inStock ?? false,
                  disabled: variantMetaMap[choiceKey]?.disabled ?? false,
                }
              })

              accumulator[key].push({
                key: option.key,
                label:
                  siteProperties[productOptionTypeMap[option.key].propertyKey]
                    ?.value || option.key,
                placeholder:
                  siteProperties[
                    productOptionTypeMap[option.key].placeholderPropertyKey
                  ]?.value || null,
                type:
                  productOptionTypeMap[option.key]?.type ||
                  ProductChoiceType.DROPDOWN,
                attribute: optionKeysToApplyAttribute?.includes(option.key)
                  ? true
                  : null,
                choices: transformedChoices,
              })
            }
          })
        }
        return accumulator
      },
      {},
    )

    accumulatorInitialValue =
      product.options.length === 1
        ? {
            0: Object.keys(linkedOnChoiceKeys).length
              ? linkedOnChoiceKeys[product.options[0].key]
              : product.options,
          }
        : {
            ...linkedOnChoiceKeys,
            0: product.options.map((option) => ({
              key: option.key,
              label:
                siteProperties[productOptionTypeMap[option.key].propertyKey]
                  ?.value || option.key,
              placeholder:
                siteProperties[
                  productOptionTypeMap[option.key].placeholderPropertyKey
                ]?.value || null,
              type:
                productOptionTypeMap[option.key]?.type ||
                ProductChoiceType.DROPDOWN,
              attribute: optionKeysToApplyAttribute?.includes(option.key)
                ? true
                : null,
              choices: option.choices.map((choice) => {
                const key = generateChoicesKey([choice])

                const image = variantMetaMap[key]?.thumbnailImage

                return {
                  ...choice,
                  image: (choice.colour && image) || null,
                  inStock: true,
                  disabled: false,
                }
              }),
            })),
          }
  }

  const indexedVariantChoices = product.variants.reduce<VariantChoices>(
    (variantAccumulator, variant) => {
      variantAccumulator[variant.sku] = product.options.reduce<Options>(
        (optionAccumulator, option) => {
          if (optionKeysToFilter?.includes(option.key)) {
            return optionAccumulator
          }

          const key = generateChoicesKey(variant.choices)

          const currentOptionChoices = variant.choices.filter(
            (choice) => choice.optionKey !== option.key,
          )

          const choices =
            variantMetaMap[key].supportedChoices &&
            variantMetaMap[key].supportedChoices[option.key]
              ? variantMetaMap[key].supportedChoices[option.key]
              : option.choices

          const transformedChoices = choices.map((choice) => {
            // @TODO: REBUILD-5103
            // Horrible workaround for short hex codes.
            // Validates they are 1-2 char hexes and pads them out
            if (
              choice.colour &&
              choice.colour.startsWith('#') &&
              choice.colour.length < 7
            ) {
              const hexStart = choice.colour.substring(1, 3)
              const validateHex = '#'.padEnd(choice.colour.length, hexStart)
              if (validateHex === choice.colour) {
                choice.colour = '#'.padEnd(7, hexStart)
              }
            }

            const choices = [...currentOptionChoices, choice]
            const key = generateChoicesKey(choices)
            let image = variantMetaMap[key]?.thumbnailImage

            if (!image) {
              const fallbackKey = generateChoicesKey(
                choices.filter(
                  (choice) => choice.optionKey === product.linkedOn,
                ),
              )
              image = variantMetaMap[fallbackKey]?.thumbnailImage
            }

            return {
              ...choice,
              image: (choice.colour && image) || null,
              inStock: variantMetaMap[key]?.inStock ?? false,
              disabled: variantMetaMap[key]?.disabled ?? false,
              notifyWhenInStockEnabled:
                variantMetaMap[key]?.notifyWhenInStockEnabled ?? false,
            }
          })

          optionAccumulator.push({
            key: option.key,
            label:
              siteProperties[productOptionTypeMap[option.key].propertyKey]
                ?.value || option.key,
            placeholder:
              siteProperties[
                productOptionTypeMap[option.key].placeholderPropertyKey
              ]?.value || null,
            type:
              productOptionTypeMap[option.key]?.type ||
              ProductChoiceType.DROPDOWN,
            attribute: optionKeysToApplyAttribute?.includes(option.key)
              ? true
              : null,
            choices: transformedChoices,
          })
          return optionAccumulator
        },
        [],
      )
      return variantAccumulator
    },
    accumulatorInitialValue,
  )

  return { choiceMap, options: indexedVariantChoices }
}
